import { useState, useRef } from 'react';
import { Send, PhoneCall } from 'lucide-react';
import { Link } from 'react-router-dom';

interface DevisFormProps {
  defaultCommune?: string;
}

export function DevisForm({ defaultCommune = '' }: DevisFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check
    const gotchaInput = form.querySelector<HTMLInputElement>('input[name="_gotcha"]');
    if (gotchaInput && gotchaInput.value.trim() !== '') {
      setStatus('success');
      setStatusMessage('Votre demande a bien été envoyée. Nous vous contacterons rapidement !');
      form.reset();
      return;
    }

    setSubmitting(true);
    setStatus('idle');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://formspree.io/f/mykqvjoz', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setStatus('success');
        setStatusMessage('Votre demande a bien été envoyée. Nous vous contacterons rapidement !');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || 'Une erreur est survenue. Veuillez réessayer.';
        setStatus('error');
        setStatusMessage(serverMsg);
      }
    } catch {
      setStatus('error');
      setStatusMessage('Erreur de connexion. Veuillez réessayer ou nous appeler au 0800 20 410.');
    } finally {
      setSubmitting(false);
    }
  };

  const panneTypes = [
    'Volet bloqué',
    'Ne remonte plus',
    'Moteur HS',
    'Sangle ou manivelle cassée',
    'Télécommande HS',
    'Lames ou tablier abîmés',
    'Motorisation',
    'Autre',
  ];

  return (
    <section className="py-14 md:py-20 bg-background-100" id="devis-form">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="section-title">Demandez votre devis gratuit</h2>
          <p className="section-subtitle mt-3">
            Un problème avec votre volet roulant ? Remplissez ce formulaire et nous vous recontacterons dans les plus brefs délais.
          </p>
        </div>

        <div className="card p-6 md:p-8">
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            data-readdy-form
            noValidate
          >
            {/* Honeypot */}
            <input
              type="text"
              name="_gotcha"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
            <input type="hidden" name="_next" value="https://volet-charleroi.be/merci/" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="devis-nom" className="block text-sm font-medium text-foreground-600 mb-1.5">
                  Nom complet <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  id="devis-nom"
                  name="nom"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                  placeholder="Votre nom"
                />
              </div>
              <div>
                <label htmlFor="devis-telephone" className="block text-sm font-medium text-foreground-600 mb-1.5">
                  Téléphone <span className="text-accent-500">*</span>
                </label>
                <input
                  type="tel"
                  id="devis-telephone"
                  name="telephone"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                  placeholder="04XX XX XX XX"
                />
              </div>
              <div>
                <label htmlFor="devis-email" className="block text-sm font-medium text-foreground-600 mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  id="devis-email"
                  name="email"
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                  placeholder="votre@email.be"
                />
              </div>
              <div>
                <label htmlFor="devis-commune" className="block text-sm font-medium text-foreground-600 mb-1.5">
                  Commune <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  id="devis-commune"
                  name="commune"
                  required
                  defaultValue={defaultCommune}
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                  placeholder="Votre commune"
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="devis-panne" className="block text-sm font-medium text-foreground-600 mb-1.5">
                Type de panne <span className="text-accent-500">*</span>
              </label>
              <select
                id="devis-panne"
                name="type_panne"
                required
                className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors bg-white"
              >
                <option value="">Sélectionnez un type de panne</option>
                {panneTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="devis-message" className="block text-sm font-medium text-foreground-600 mb-1.5">
                Décrivez votre problème
              </label>
              <textarea
                id="devis-message"
                name="message"
                rows={4}
                maxLength={500}
                className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors resize-none"
                placeholder="Décrivez votre panne, le type de volet, la marque si vous la connaissez..."
              />
              <p className="text-xs text-foreground-400 mt-1">Maximum 500 caractères</p>
            </div>

            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="consentement_rgpd"
                  required
                  className="mt-0.5 w-4 h-4 text-accent-500 border-background-300 rounded focus:ring-accent-400 shrink-0"
                />
                <span className="text-sm text-foreground-600">
                  J'accepte que mes données soient collectées pour le traitement de ma demande.{' '}
                  <Link to="/politique-confidentialite/" className="text-accent-500 hover:text-accent-600 underline whitespace-nowrap">
                    Politique de confidentialité
                  </Link>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full sm:w-auto px-8 py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Envoi en cours...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Envoyer ma demande
                </span>
              )}
            </button>

            {status === 'success' && (
              <div className="mt-4 p-4 bg-success-50 text-success-600 rounded-lg text-sm font-medium">
                {statusMessage}
              </div>
            )}
            {status === 'error' && (
              <div className="mt-4 p-4 bg-red-50 text-red-600 rounded-lg text-sm">
                <p className="font-medium mb-1">Erreur</p>
                <p>{statusMessage}</p>
              </div>
            )}
          </form>

          <div className="mt-6 pt-6 border-t border-background-200 text-center">
            <p className="text-sm text-foreground-500">
              Ou appelez-nous directement :
            </p>
            <a
              href="tel:+3280020410"
              className="inline-flex items-center gap-2 mt-2 text-lg font-semibold text-accent-500 hover:text-accent-600 transition-colors"
            >
              <PhoneCall className="w-5 h-5" />
              0800 20 410
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}